import { useTranslation } from '@govard/i18n-core/react';
// Подтверждение адреса по ссылке из письма.
//
// Подтверждает сразу при открытии: у страницы одно действие, и просить нажать
// ещё одну кнопку после перехода по ссылке незачем.
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Button, Card, Spin, Typography } from 'antd';

import { verifyEmail } from '../api/account.client.js';
import { AuthMark } from '../components/AuthMark.js';
import { LanguageSwitcher } from '../../../components/layout/LanguageSwitcher.js';

const { Title } = Typography;

type State = { status: 'pending' } | { status: 'ok'; email: string } | { status: 'failed' };

export function VerifyEmailPage(): ReactElement {
  const { t } = useTranslation();
  const { token = '' } = useParams();
  const [state, setState] = useState<State>({ status: 'pending' });
  // React 18 в dev монтирует эффекты дважды, а токен одноразовый: второй вызов
  // получил бы «ссылка больше не действует» на только что успешном подтверждении.
  const started = useRef(false);

  useEffect(() => {
    if (!token || started.current) return;
    started.current = true;

    verifyEmail(token)
      .then((result) => setState({ status: 'ok', email: result.email }))
      .catch(() => setState({ status: 'failed' }));
  }, [token]);

  return (
    <main
      className="relative flex min-h-screen items-center justify-center p-6"
      style={{ backgroundColor: 'var(--ant-color-bg-layout)' }}
    >
      <div className="absolute right-6 top-6" data-testid="public-language-switcher-position">
        <LanguageSwitcher />
      </div>
      <Card style={{ maxWidth: 440, width: '100%' }}>
        <div className="mb-4 flex items-center gap-3">
          <AuthMark />
          <Title level={4} style={{ margin: 0 }}>
            {t('crm.auth.verify.title')}
          </Title>
        </div>

        {state.status === 'pending' ? (
          <div className="flex justify-center py-6">
            <Spin />
          </div>
        ) : state.status === 'ok' ? (
          <div className="flex flex-col gap-3">
            <Alert
              type="success"
              showIcon
              title={t('crm.auth.verify.success_title')}
              description={t('crm.auth.verify.success_description', { email: state.email })}
            />
            <Link to="/dashboard">
              <Button type="primary">{t('crm.auth.verify.open_app')}</Button>
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <Alert
              type="warning"
              showIcon
              title={t('crm.auth.verify.failed_title')}
              description={t('crm.auth.verify.failed_description')}
            />
            <Link to="/settings">
              <Button>{t('crm.auth.verify.settings')}</Button>
            </Link>
          </div>
        )}
      </Card>
    </main>
  );
}
